import { Box, Card, Skeleton, Typography } from '@compass/core';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EscalatorWarningIcon from '@mui/icons-material/EscalatorWarning';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PersonIcon from '@mui/icons-material/Person';
import { alpha, Theme } from '@mui/material/styles';
import { DashboardKpi } from './types';
import { formatKpiValue } from './formatKpiValue';

interface KpiCardProps {
    kpi?: DashboardKpi;
    loading?: boolean;
}

const KPI_ICON_MAP: Record<string, React.ElementType> = {
    totalParticipantsServed: PeopleAltIcon,
    totalServicesDelivered: CalendarMonthIcon,
    totalHoursDelivered: AccessTimeIcon,
    averageHoursPerParticipant: PersonIcon,
    averageSessionsPerParticipant: EventNoteIcon,
    participantsWithoutServices: PeopleAltIcon,
};

const KPI_UNIT_MAP: Record<string, string> = {
    totalParticipantsServed: 'Participants',
    totalServicesDelivered: 'Services',
    totalHoursDelivered: 'Hours',
    averageHoursPerParticipant: 'Hours / participant',
    averageSessionsPerParticipant: 'Sessions / participant',
    participantsWithoutServices: 'Participants',
};

export const KpiCard = ({ kpi, loading = false }: KpiCardProps) => {
    const cardSx = {
        width: '100%',
        height: '100%',
        minHeight: { xs: 168, sm: 250 },
        boxSizing: 'border-box',
        p: 0,
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        boxShadow: (theme: Theme) => `0 3px 12px ${alpha(theme.palette.common.black, 0.045)}`,
    } as const;

    if (loading || !kpi) {
        return <Card sx={cardSx}><Box sx={{ height: '100%', boxSizing: 'border-box', p: { xs: 2.5, sm: 3 } }}><Skeleton variant="rounded" height="100%" /></Box></Card>;
    }

    const trend = {
        up: { color: 'success.dark', icon: ArrowUpwardIcon },
        down: { color: 'error.main', icon: ArrowDownwardIcon },
        neutral: { color: 'text.secondary', icon: ArrowForwardIcon },
    }[kpi.trend];
    const MetricIcon = kpi.viewType === 'parent'
        ? EscalatorWarningIcon
        : KPI_ICON_MAP[kpi.id] ?? EventNoteIcon;
    const TrendIcon = trend.icon;

    return (
        <Card sx={cardSx}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Box sx={{ flex: 1, display: 'grid', gridTemplateRows: { xs: '56px 66px', sm: '60px 72px' }, alignContent: 'center', rowGap: { xs: 1.5, sm: 2 }, p: { xs: 3, sm: 3.5 }, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                        <Box sx={{ flex: '0 0 auto', width: { xs: 44, sm: 48 }, height: { xs: 44, sm: 48 }, borderRadius: '50%', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08), color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MetricIcon sx={{ fontSize: { xs: 26, sm: 29 } }} />
                        </Box>
                        <Box sx={{ minWidth: 0, minHeight: { xs: 44, sm: 48 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            {kpi.viewType === 'parent' && (
                                <Typography sx={{ color: 'primary.main', fontSize: { xs: 9, sm: 10 }, fontWeight: 600, lineHeight: 1.15, letterSpacing: '0.12em', textTransform: 'uppercase', mb: 0.5 }}>
                                    Parent
                                </Typography>
                            )}
                            <Typography sx={{ color: 'text.primary', fontSize: { xs: 14, sm: 15 }, fontWeight: 700, lineHeight: 1.2, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }}>
                                {kpi.label}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', columnGap: { xs: 1.5, sm: 2 }, mx: { xs: -0.5, sm: -0.75 }, minWidth: 0, width: { xs: 'calc(100% + 6px)', sm: 'calc(100% + 9px)' }, height: '100%' }}>
                    <Box sx={{ minWidth: 0, display: 'grid', gridTemplateRows: '42px 24px', alignItems: 'center', justifyItems: 'center', textAlign: 'center' }}>
                        <Typography sx={{ color: 'primary.dark', fontSize: { xs: 37, sm: 40 }, fontWeight: 800, lineHeight: 0.95, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                            {formatKpiValue(kpi.currentValue, kpi.format)}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: { xs: 9.5, sm: 10 }, fontWeight: 600, lineHeight: 1.2, letterSpacing: '0.065em', textTransform: 'uppercase', minHeight: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowWrap: 'anywhere' }}>
                            {KPI_UNIT_MAP[kpi.id] ?? kpi.label}
                        </Typography>
                    </Box>

                    <Box sx={{ minWidth: 0, height: 66, alignSelf: 'center', display: 'grid', gridTemplateRows: '42px 24px', alignItems: 'center', justifyItems: 'center', textAlign: 'center', borderLeft: 1, borderColor: 'border.main', pl: { xs: 0.75, sm: 1 } }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.65, maxWidth: '100%', minHeight: 34, boxSizing: 'border-box', px: 2, py: 1, borderRadius: 1.5, color: trend.color, bgcolor: (theme) => alpha(kpi.trend === 'up' ? theme.palette.success.main : kpi.trend === 'down' ? theme.palette.error.main : theme.palette.text.secondary, 0.1) }}>
                            <TrendIcon sx={{ flex: '0 0 auto', fontSize: 18 }} />
                            <Typography color="inherit" sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                {kpi.percentChange}%
                            </Typography>
                        </Box>
                        <Typography component="div" sx={{ color: 'text.secondary', fontSize: { xs: 11, sm: 12 }, lineHeight: 1.25, whiteSpace: 'nowrap', alignSelf: 'start' }}>
                            {kpi.comparisonLabel}{' '}
                            <Box component="span" sx={{ color: 'primary.dark', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                {formatKpiValue(kpi.previousValue, kpi.format)}
                            </Box>
                        </Typography>
                    </Box>
                    </Box>
                </Box>

            </Box>
        </Card>
    );
};
